import { AppLink } from "@/components/app-link";
import { AlertCircle, ArrowLeft } from "lucide-react";

import { PageHeader } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface SubmittalStateGateProps {
  jobId: string;
  job:
    | {
        _id: string;
        name: string;
        status: "pending" | "processing" | "ready_for_review" | "completed" | "failed";
        error?: string;
      }
    | null
    | undefined;
  submittal: { hasEditedData?: boolean } | null | undefined;
  isLoadingCSV: boolean;
  generatedData: unknown;
  editedData: unknown;
  csvError: string | null;
  isHydratingEdited: boolean;
  editedPayloadError: string | null;
  activeVersion: "generated" | "edited";
  hasEditedData: boolean;
}

export function getSubmittalStateGate({
  jobId,
  job,
  submittal,
  isLoadingCSV,
  generatedData,
  editedData,
  csvError,
  isHydratingEdited,
  editedPayloadError,
  activeVersion,
  hasEditedData,
}: SubmittalStateGateProps) {
  if (job === undefined) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Loading..."
          breadcrumbs={[{ label: "SpecPilot", href: "/specPilot" }, { label: "Loading..." }]}
        />
        <Card>
          <ProcessingSpinner message="Loading submittal..." />
        </Card>
      </div>
    );
  }

  if (job === null) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Job Not Found"
          breadcrumbs={[{ label: "SpecPilot", href: "/specPilot" }, { label: "Not Found" }]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <AppLink to="/specPilot">
            <ArrowLeft className="size-4" />
            Back to SpecPilot
          </AppLink>
        </Button>
      </div>
    );
  }

  if (submittal === undefined) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Submittal"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
            { label: "Submittal" },
          ]}
        />
        <Card>
          <ProcessingSpinner message="Loading submittal..." />
        </Card>
      </div>
    );
  }

  if (submittal === null) {
    if (job.status === "pending" || job.status === "processing") {
      const message = "Generating submittal...";

      return (
        <div className="space-y-6">
          <PageHeader
            title="Submittal"
            breadcrumbs={[
              { label: "SpecPilot", href: "/specPilot" },
              { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
              { label: "Submittal" },
            ]}
          />
          <Card>
            <ProcessingSpinner message={message} />
          </Card>
        </div>
      );
    }

    if (job.status === "ready_for_review") {
      return (
        <div className="space-y-6">
          <PageHeader
            title="Submittal"
            breadcrumbs={[
              { label: "SpecPilot", href: "/specPilot" },
              { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
              { label: "Submittal" },
            ]}
          />
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal is missing review data</AlertTitle>
            <AlertDescription>
              The job reached review, but no submittal record is available. Return to the job
              detail page and retry generation.
            </AlertDescription>
          </Alert>
          <Button asChild variant="outline">
            <AppLink to="/specPilot/$jobId" params={{ jobId }}>
              <ArrowLeft className="size-4" />
              Back to Job
            </AppLink>
          </Button>
        </div>
      );
    }

    if (job.status === "completed") {
      return (
        <div className="space-y-6">
          <PageHeader
            title="Submittal"
            breadcrumbs={[
              { label: "SpecPilot", href: "/specPilot" },
              { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
              { label: "Submittal" },
            ]}
          />
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal is missing generated output</AlertTitle>
            <AlertDescription>
              The job completed, but no generated submittal record is available. Return to the job
              detail page and retry generation.
            </AlertDescription>
          </Alert>
          <Button asChild variant="outline">
            <AppLink to="/specPilot/$jobId" params={{ jobId }}>
              <ArrowLeft className="size-4" />
              Back to Job
            </AppLink>
          </Button>
        </div>
      );
    }

    if (job.status === "failed") {
      return (
        <div className="space-y-6">
          <PageHeader
            title="Submittal"
            breadcrumbs={[
              { label: "SpecPilot", href: "/specPilot" },
              { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
              { label: "Submittal" },
            ]}
          />
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Submittal generation failed</AlertTitle>
            <AlertDescription>
              {job.error ?? "The submittal could not be generated. Please return to the job and try again."}
            </AlertDescription>
          </Alert>
          <Button asChild variant="outline">
            <AppLink to="/specPilot/$jobId" params={{ jobId }}>
              <ArrowLeft className="size-4" />
              Back to Job
            </AppLink>
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <PageHeader
          title="Submittal"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
            { label: "Submittal" },
          ]}
        />
        <Alert>
          <AlertCircle className="size-4" />
          <AlertTitle>No submittal generated</AlertTitle>
          <AlertDescription>
            This job does not have a submittal yet. Generate one from the job detail page.
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <AppLink to="/specPilot/$jobId" params={{ jobId }}>
            <ArrowLeft className="size-4" />
            Back to Job
          </AppLink>
        </Button>
      </div>
    );
  }

  const showLoading =
    isLoadingCSV ||
    (submittal && !generatedData && !csvError) ||
    (submittal?.hasEditedData && !editedData && !editedPayloadError) ||
    (activeVersion === "edited" && hasEditedData && !editedData && isHydratingEdited);

  if (showLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Submittal"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
            { label: "Submittal" },
          ]}
        />
        <Card>
          <ProcessingSpinner message="Loading submittal data..." />
        </Card>
      </div>
    );
  }

  if (csvError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Submittal"
          breadcrumbs={[
            { label: "SpecPilot", href: "/specPilot" },
            { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
            { label: "Submittal" },
          ]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Failed to load submittal</AlertTitle>
          <AlertDescription>{csvError}</AlertDescription>
        </Alert>
        <Button asChild variant="outline">
          <AppLink to="/specPilot/$jobId" params={{ jobId }}>
            <ArrowLeft className="size-4" />
            Back to Job
          </AppLink>
        </Button>
      </div>
    );
  }

  return null;
}
